import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';

export const detectIOSContext = () => {
    if (typeof navigator === 'undefined') {
        return {
            isIOS: false,
            inAppWebView: false,
            isStandalonePWA: false,
            browser: 'unknown'
        };
    }

    const ua = navigator.userAgent || '';
    const isIPadOS13Plus = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
    const isIOSDevice = /iPhone|iPad|iPod/.test(ua) || isIPadOS13Plus;

    const isCriOS = /CriOS/.test(ua);
    const isFxiOS = /FxiOS/.test(ua);
    const isEdgiOS = /EdgiOS/.test(ua);

    const looksLikeSafari =
        /Safari/.test(ua) &&
        /Version\/\d+/.test(ua) &&
        !isCriOS && !isFxiOS && !isEdgiOS;

    const isStandalonePWA = typeof window !== 'undefined' ? !!window.navigator.standalone : false;

    const isFacebook = /FBAN|FBAV/.test(ua);
    const isInstagram = /Instagram/.test(ua);
    const isTwitter = /Twitter/.test(ua);
    const isTikTok = /TikTok/.test(ua);
    const isGoogleApp = /GSA/.test(ua);
    const isInKnownAppWebView =
        isFacebook || isInstagram || isTwitter || isTikTok || isGoogleApp;

    const likelyInWebView =
        isIOSDevice &&
        !isStandalonePWA &&
        !looksLikeSafari &&
        !isCriOS && !isFxiOS && !isEdgiOS;

    const hasWKMessageHandlers =
        typeof window !== 'undefined' && !!(window.webkit && window.webkit.messageHandlers);

    const inAppWebView = isInKnownAppWebView || likelyInWebView || hasWKMessageHandlers;

    let browser = 'Safari';
    if (isCriOS) browser = 'Chrome (iOS)';
    else if (isFxiOS) browser = 'Firefox (iOS)';
    else if (isEdgiOS) browser = 'Edge (iOS)';
    else if (!looksLikeSafari) browser = 'Unknown WKWebView';

    return {
        isIOS: isIOSDevice,
        inAppWebView,
        isStandalonePWA,
        browser
    };
};

const WEBAPP_URL = 'https://script.google.com/macros/s/AKfycbx7DdWve6rF-zCsJxJlU7Rq6Hd5T-9BPDbbFZQ0S7Q9DzO7QNj_sVVkeSi0k0ol_m5CyA/exec';

const REASON_OPTIONS = [
    { value: 'contest_entry', label: 'Contest Entry' },
    { value: 'news_tip', label: 'News Tip' },
    { value: 'its_a_new_day', label: 'It\'s a New Day (Some Voice Messages May Not Be Viewed)' },
    { value: 'community_event', label: 'Community Event' },
    { value: 'other', label: 'Other' }
];

const MAX_RECORDING_MS = 7 * 60 * 1000; // 7 minutes

const VoiceMessageModal = ({ onRecordingStart = null, onRecordingStop = null } = {}) => {
    const [reason, setReason] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [isRecording, setIsRecording] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [hasRecording, setHasRecording] = useState(false);
    const [showProgress, setShowProgress] = useState(false);
    const [progress, setProgress] = useState(0);
    const [logLines, setLogLines] = useState([]);
    const [audioSrc, setAudioSrc] = useState(null);
    const [blockedEnvironment, setBlockedEnvironment] = useState(false);

    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const startedAtRef = useRef(0);
    const audioUrlRef = useRef(null);
    const lastRecordingRef = useRef(null);
    const lastUserInfoRef = useRef(null);
    const onRecordingStartRef = useRef(onRecordingStart);
    const onRecordingStopRef = useRef(onRecordingStop);
    const stopNotifiedRef = useRef(false);
    const recordingTimeoutRef = useRef(null);

    const notifyRecordingStopped = useCallback(() => {
        if (stopNotifiedRef.current) {
            return;
        }
        stopNotifiedRef.current = true;
        const stopCallback = onRecordingStopRef.current;
        if (typeof stopCallback === 'function') {
            stopCallback();
        }
    }, []);

    useEffect(() => {
        onRecordingStartRef.current = onRecordingStart;
    }, [onRecordingStart]);

    useEffect(() => {
        onRecordingStopRef.current = onRecordingStop;
    }, [onRecordingStop]);

    const appendLog = useCallback((...args) => {
        const message = args.join(' ');
        setLogLines(prev => [...prev, message]);
    }, []);

    const assertUserInfo = useCallback(() => {
        const info = {
            reason: reason.trim(),
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            phone: phone.trim()
        };
        if (!info.reason) {
            appendLog('Please select a reason for your message.');
            return null;
        }
        if (!info.firstName || !info.lastName || !info.phone) {
            appendLog('Please enter your first name, last name, and phone number before recording.');
            return null;
        }
        return info;
    }, [appendLog, firstName, lastName, phone, reason]);

    const pickMime = () => {
        const prefs = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
        if (typeof window === 'undefined' || typeof MediaRecorder === 'undefined') {
            return undefined;
        }
        for (const type of prefs) {
            if (MediaRecorder.isTypeSupported?.(type)) {
                return type;
            }
        }
        return undefined;
    };

    const updateAudioSrc = useCallback((url) => {
        if (audioUrlRef.current) {
            URL.revokeObjectURL(audioUrlRef.current);
            audioUrlRef.current = null;
        }
        if (url) {
            audioUrlRef.current = url;
        }
        setAudioSrc(url || null);
    }, []);

    useEffect(() => {
        return () => {
            if (recordingTimeoutRef.current) {
                clearTimeout(recordingTimeoutRef.current);
                recordingTimeoutRef.current = null;
            }
            const recorder = mediaRecorderRef.current;
            if (recorder && recorder.state !== 'inactive') {
                recorder.stop();
                notifyRecordingStopped();
                recorder.removeEventListener('stop', notifyRecordingStopped);
            }
            if (recorder?.stream) {
                recorder.stream.getTracks().forEach(track => track.stop());
            }
            if (audioUrlRef.current) {
                URL.revokeObjectURL(audioUrlRef.current);
                audioUrlRef.current = null;
            }
        };
    }, [notifyRecordingStopped]);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const context = detectIOSContext();
        if (context.isIOS && context.inAppWebView) {
            setBlockedEnvironment(true);
        }
    }, []);

    const handleStart = async () => {
        if (isRecording || isUploading) {
            return;
        }
        if (!navigator.mediaDevices?.getUserMedia) {
            appendLog('Audio recording is not supported on this device.');
            return;
        }
        if (typeof MediaRecorder === 'undefined') {
            appendLog('Audio recording is not available in this browser.');
            return;
        }
        const info = assertUserInfo();
        if (!info) {
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mimeType = pickMime();
            const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : {});
            mediaRecorderRef.current = recorder;
            chunksRef.current = [];
            stopNotifiedRef.current = false;
            recorder.ondataavailable = event => {
                if (event.data && event.data.size > 0) {
                    chunksRef.current.push(event.data);
                }
            };
            recorder.onerror = event => {
                const message = event.error?.message || String(event.error || 'Unknown recorder error');
                appendLog('Recorder error:', message);
                setIsRecording(false);
                if (recordingTimeoutRef.current) {
                    clearTimeout(recordingTimeoutRef.current);
                    recordingTimeoutRef.current = null;
                }
                notifyRecordingStopped();
            };
            recorder.addEventListener('stop', notifyRecordingStopped);
            recorder.start();
            startedAtRef.current = Date.now();
            const startCallback = onRecordingStartRef.current;
            if (typeof startCallback === 'function') {
                startCallback();
            }
            setIsRecording(true);
            setHasRecording(false);
            setShowProgress(false);
            setProgress(0);
            updateAudioSrc(null);
            appendLog('Recording...');
            if (recordingTimeoutRef.current) {
                clearTimeout(recordingTimeoutRef.current);
            }
            if (typeof window !== 'undefined') {
                recordingTimeoutRef.current = window.setTimeout(() => {
                    appendLog('Maximum recording time reached (7 minutes). Stopping recording automatically.');
                    handleStop();
                }, MAX_RECORDING_MS);
            }
            lastUserInfoRef.current = info;
            lastRecordingRef.current = null;
        } catch (error) {
            appendLog('Mic error:', error?.message || String(error));
        }
    };

    const handleStop = async () => {
        if (recordingTimeoutRef.current) {
            clearTimeout(recordingTimeoutRef.current);
            recordingTimeoutRef.current = null;
        }
        const recorder = mediaRecorderRef.current;
        if (!recorder || recorder.state === 'inactive') {
            return;
        }
        const info = lastUserInfoRef.current || assertUserInfo();
        if (!info) {
            return;
        }
        setIsRecording(false);
        notifyRecordingStopped();
        const waitForStop = new Promise(resolve => {
            const onStop = () => {
                recorder.removeEventListener('stop', onStop);
                resolve();
            };
            recorder.addEventListener('stop', onStop);
        });
        recorder.stop();
        await waitForStop;
        recorder.removeEventListener('stop', notifyRecordingStopped);
        if (recorder.stream) {
            recorder.stream.getTracks().forEach(track => track.stop());
        }
        mediaRecorderRef.current = null;

        const chunks = chunksRef.current;
        if (!chunks.length) {
            appendLog('No audio data captured.');
            return;
        }
        const mime = chunks[0]?.type || 'application/octet-stream';
        const blob = new Blob(chunks, { type: mime });
        const durationSeconds = Math.max(1, Math.round((Date.now() - startedAtRef.current) / 1000));
        const previewUrl = URL.createObjectURL(blob);
        updateAudioSrc(previewUrl);

        if (blob.size > 48 * 1024 * 1024) {
            appendLog('File too large for one request (~50MB limit). Record a shorter message.');
            setHasRecording(false);
            lastRecordingRef.current = null;
            return;
        }

        lastRecordingRef.current = { blob, mime, info, durationSeconds };
        setHasRecording(true);
        appendLog(`Recording ready (${(blob.size / 1024 / 1024).toFixed(2)} MB, ~${durationSeconds}s). Click Upload to send.`);
    };

    const toBase64 = (blob, onProgress) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result ? String(reader.result) : '';
                resolve(result.split(',')[1] || '');
            };
            reader.onerror = () => reject(reader.error || new Error('Unable to read recording.'));
            reader.onprogress = event => {
                if (event.lengthComputable && onProgress) {
                    const percent = Math.round((event.loaded / event.total) * 100);
                    onProgress(percent);
                }
            };
            reader.readAsDataURL(blob);
        });
    };

    const handleUpload = async () => {
        const recording = lastRecordingRef.current;
        if (!recording) {
            appendLog('No recording ready to upload.');
            return;
        }
        setIsUploading(true);
        setShowProgress(true);
        setProgress(0);
        let userMessage = 'Upload complete. Thanks for your message!';
        try {
            const { blob, mime, info, durationSeconds } = recording;
            const ext = mime.includes('mp4') ? 'm4a' : 'webm';
            const reasonOption = REASON_OPTIONS.find(option => option.value === info.reason);
            const reasonLabel = reasonOption ? reasonOption.label : info.reason;
            const nameParts = [reasonLabel, info.firstName, info.lastName, info.phone]
                .filter(Boolean)
                .map(part => part
                    .replace(/[^a-zA-Z0-9\s-]/g, '')
                    ?.trim()
                    .replace(/\s+/g, ' '))
                .filter(Boolean);
            const baseName = nameParts.join(' - ') || 'voice-message';
            const safeBaseName = baseName.replace(/\s+/g, '_');
            const fileName = `${safeBaseName}.${ext}`;
            appendLog(`Uploading ${fileName}...`);
            const base64 = await toBase64(blob, value => setProgress(value));
            const response = await fetch(WEBAPP_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'text/plain;charset=utf-8' },
                body: JSON.stringify({
                    name: fileName,
                    mime,
                    base64,
                    meta: {
                        durationSec: durationSeconds,
                        recordedAt: new Date().toISOString(),
                        reason: info.reason
                    },
                    user: info
                })
            });

            const contentType = response.headers.get('content-type') || '';
            let payload = null;
            let rawText = null;

            if (contentType.includes('application/json')) {
                payload = await response.json().catch(() => null);
            } else {
                rawText = await response.text().catch(() => null);
                if (rawText) {
                    try {
                        payload = JSON.parse(rawText);
                    } catch (_) {
                        // keep raw text for logging purposes only
                    }
                }
            }

            const uploadSucceeded = response.ok && (payload?.ok !== false);
            if (!uploadSucceeded) {
                const message = payload?.error || rawText || `${response.status} ${response.statusText}`;
                console.error('Upload failed:', message);
            } else {
                const driveUrl = payload?.url || payload?.driveUrl || payload?.fileUrl;
                userMessage = driveUrl ? `Upload complete. Drive URL: ${driveUrl}` : 'Upload complete. Thanks for your message!';
            }
        } catch (error) {
            console.error('Upload error:', error?.message || String(error));
        } finally {
            appendLog(userMessage);
            lastRecordingRef.current = null;
            setHasRecording(false);
            setIsUploading(false);
            setShowProgress(false);
            setProgress(0);
        }
    };

    const statusDetails = useMemo(() => {
        if (isRecording) {
            return {
                label: 'Recording in progress…',
                tone: 'active',
                icon: 'fiber_manual_record'
            };
        }
        if (isUploading) {
            return {
                label: 'Uploading your message…',
                tone: 'uploading',
                icon: 'cloud_upload'
            };
        }
        if (hasRecording) {
            return {
                label: 'Recording ready to upload',
                tone: 'ready',
                icon: 'check_circle'
            };
        }
        return {
            label: 'Ready to record',
            tone: 'idle',
            icon: 'mic_none'
        };
    }, [hasRecording, isRecording, isUploading]);

    const hasLogs = logLines.length > 0;

    if (blockedEnvironment) {
        return (
            <div className='voice-modal voice-modal-blocked'>
                <div className='voice-blocked'>
                    <i className='material-icons'>report_problem</i>
                    <h2>Open in your browser</h2>
                    <p>Voice messages aren&apos;t supported in the JZ 94.5 app yet. Visit wjzd.com/voice to record your message at this time.</p>
                    <p className='voice-blocked-link'>wjzd.com/voice</p>
                </div>
            </div>
        );
    }

    return (
        <div className='voice-modal'>
            {hasLogs ? (
                <div className='voice-log'>
                    <div className='voice-log-header'>
                        <i className='material-icons'>event_note</i>
                        <span>Session updates</span>
                    </div>
                    <ul>
                        {logLines.map((line, index) => (
                            <li key={`${line}-${index}`}>{line}</li>
                        ))}
                    </ul>
                </div>
            ) : null}


            <div className={`voice-status voice-status-${statusDetails.tone}`}>
                <i className='material-icons'>{statusDetails.icon}</i>
                <span>{statusDetails.label}</span>
            </div>

            {audioSrc ? (
                <div className='voice-preview'>
                    <div className='voice-preview-label'>
                        <i className='material-icons'>play_circle</i>
                        <span>Preview recording</span>
                    </div>
                    <audio className='voice-player' src={audioSrc} controls />
                </div>
            ) : null}

            <div className='voice-field voice-reason'>
                <label htmlFor='voice-reason'>Reason for your message</label>
                <div className='voice-input-wrapper'>

                    <select
                        id='voice-reason'
                        value={reason}
                        onChange={event => setReason(event.target.value)}
                        required
                    >
                        <option value='' disabled>Select a reason</option>
                        {REASON_OPTIONS.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            <div className='voice-inputs'>
                <div className='voice-field'>
                    <label htmlFor='voice-first-name'>First name</label>
                    <div className='voice-input-wrapper'>
                        <input
                            id='voice-first-name'
                            type='text'
                            value={firstName}
                            onChange={event => setFirstName(event.target.value)}
                            placeholder='e.g. First Name'
                            autoComplete='given-name'
                            required
                        />
                    </div>
                </div>
                <div className='voice-field'>
                    <label htmlFor='voice-last-name'>Last name</label>
                    <div className='voice-input-wrapper'>
                        <input
                            id='voice-last-name'
                            type='text'
                            value={lastName}
                            onChange={event => setLastName(event.target.value)}
                            placeholder='e.g. Last Name'
                            autoComplete='family-name'
                            required
                        />
                    </div>
                </div>
                <div className='voice-field'>
                    <label htmlFor='voice-phone'>Phone number</label>
                    <div className='voice-input-wrapper'>
                        <input
                            id='voice-phone'
                            type='tel'
                            value={phone}
                            onChange={event => setPhone(event.target.value)}
                            placeholder='(###) ###-####'
                            autoComplete='tel'
                            required
                        />
                    </div>
                </div>
            </div>

            <div className='voice-actions'>
                <button className='voice-button voice-button-primary' onClick={handleStart} disabled={isRecording || isUploading}>
                    <i className='material-icons'>mic</i>
                    <span>Start recording</span>
                </button>
                <button className='voice-button voice-button-danger' onClick={handleStop} disabled={!isRecording}>
                    <i className='material-icons'>stop</i>
                    <span>Stop</span>
                </button>
                <button className='voice-button voice-button-secondary' onClick={handleUpload} disabled={!hasRecording || isUploading}>
                    <i className='material-icons'>cloud_upload</i>
                    <span>Upload message</span>
                </button>
            </div>



            {showProgress ? (
                <div className='voice-progress-container'>
                    <progress className='voice-progress' max='100' value={progress} />
                    <span className='voice-progress-text'>{progress}%</span>
                </div>
            ) : null}


        </div>
    );
};

export default VoiceMessageModal;
